import enLocale from "@fullcalendar/core/locales/en-gb";
import svLocale from "@fullcalendar/core/locales/sv";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import FullCalendar, {
  DatesSetArg,
  EventClickArg,
  EventInput
} from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import { format, isBefore } from "date-fns";
import { inject, observer } from "mobx-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { Cell, Paper, Text, MeetingTypeIcon } from "src/components";
import Box from "react-raster";
import { theme } from "src/config";
import { Meeting } from "src/models";
import { AuthStore, MeetingStore, SpaceStore } from "src/stores";
import { MeetingCategory } from "src/types";
import styled, { css } from "styled-components";
export interface IMeetingViewProps {
  authStore?:    AuthStore;
  meetingStore?: MeetingStore;
  spaceStore?:   SpaceStore;
}

export const MeetingView = inject("authStore", "meetingStore", "spaceStore",)(observer((props: IMeetingViewProps): JSX.Element => {
  const { meetingStore, authStore, spaceStore } = props;
  const { t, i18n } = useTranslation();
  const calendarRef = React.useRef<FullCalendar>();

  const renderEventContent = React.useCallback((eventInfo: EventInput) => {
  const { extendedProps, title, start, end } = eventInfo.event;
  const formattedTime: string = start && format(start, "HH:mm");
  const formattedEnd:  string  = end && format(end, "HH:mm");
  const isPast:        boolean       = isBefore(end, new Date());

  return (
    <EventListWrapper>
      <EventListItem
        isPast          = {isPast}
        isActiveMeeting = {Boolean(extendedProps.active)}
      >
      <Box>
        <MeetingTypeIcon type={extendedProps.type} />
      </Box>
      <Box>
        <Text size="xsmall" truncated color={"white"}>{`${formattedTime} - ${
          formattedEnd ? formattedEnd : "?"
        }`}</Text>
        <Text size="small" truncated color={"white"}>{`${title}`}</Text>
        <Text size="xxsmall" truncated color={"white"}>{`${t(extendedProps.category)}`}</Text>
        </Box>
      </EventListItem>
    </EventListWrapper>
  );
  }, []); 

  return (
    <Paper
      style={{ ...theme.paper.fullwidth, margin: theme.layout.gutter }}
      $center
      $noShadow
    >
      <CalendarContainer width={8}>
        <FullCalendar
          ref                = {calendarRef}
          firstDay           = {1}
          initialView        = "dayGridMonth"
          handleWindowResize = {true}
          weekNumberFormat   = {{ week: "numeric" }}
          plugins            = {[dayGridPlugin, interactionPlugin, timeGridPlugin]}
          aspectRatio        = {2}
          timeZone           = "UTC"
          height             = {"70rem"}
          locale             = {i18n.language}
          locales            = {[svLocale, enLocale]}
          dateClick          = {authStore.getCurUser.canEdit ? 
            (info) => {
          
              const date = info.date
              const now = new Date();
              
              date.setHours(now.getHours());
              date.setMinutes(now.getMinutes());
              date.setSeconds(now.getSeconds());

              meetingStore.setActive(
                Meeting.create({
                  date:         date,
                  leader_id:    authStore.getCurUser.id,
                  desc:         "",
                  title:        "",
                  duration :    1,
                  reminder_minutes: 15,
                  room_id:      authStore.getCurRoomID,
                  workspace_id: authStore.getCurWorkspaceID,
                  type:         "none",
                  category:     MeetingCategory.Projection,
                  start:        date,
                })
              );
              
          }
          : undefined
          }
          datesSet         = {(dateInfo: DatesSetArg) => spaceStore.setCurDate(dateInfo.view.currentStart)}
          events           = {meetingStore.getCalendarEvents}
          eventContent     = {renderEventContent} 
          eventClick       = {(arg: EventClickArg) => meetingStore.setActiveById(arg.event.id)}
          displayEventTime = {true}
          headerToolbar    = {{
            left:   "prev,next today",
            center: "title",
            right:  "dayGridMonth,timeGridWeek"
          }}
        />
      </CalendarContainer>
    </Paper>
  );
}));

const eventTimeStyles = {
  past: css`
    background-color: ${theme.color.grayDefault};
    /* filter: brightness(140%); */
    opacity: 1;
  `,
  future: css`
    background-color: ${theme.color.primary8};
    /* filter: brightness(140%); */
  `,
  active: css`
    background-color: ${theme.color.status.active};
    /* filter: brightness(100%); */
  `
};

const EventListItem = styled.div<{isPast: boolean; isActiveMeeting: boolean;}>`
  display:       flex;
  flex-direction: row;
  align-items: center;
  box-shadow:    ${theme.boxShadow.medium};
  color:         ${theme.color.white};
  height:        100%;
  border-radius: 0.3rem;
  padding:       0.5rem 1rem;
  gap:           1.5rem;
  cursor:        pointer;
  ${(p) => (p.isPast ? eventTimeStyles["past"] : eventTimeStyles["future"])};
  ${(p) => p.isActiveMeeting && eventTimeStyles["active"]};
`; 

const EventListWrapper = styled.div`
  height: 100%;
  width: 100%;
`

const CalendarContainer = styled(Cell)`
  .fc td .fc-daygrid-day.fc-day:hover {
    background: ${theme.color.grayLight};
  }

  .fc-event {
    border: none;
    background: none;
  }

  .fc-timegrid-event {
    border: none;
  }

  .fc-daygrid-dot-event:hover,
  .fc-daygrid-dot-event.fc-event-mirror {
    background: none;
  }

  .fc-event-main {
    padding: 0;
  }

  .fc-col-header {
    font-size: ${theme.font.size.small};
  }

  .fc-button {
    font-size: ${theme.font.size.small};
  }

  .fc-event-title {
    font-size: ${theme.font.size.default}
  }

  .fc-toolbar-title {
    font-size: ${theme.font.size.large};
  }

  .fc .fc-daygrid-day-events {
    font-size: ${theme.font.size.small};
  }

  .fc-event-title {
    padding: 0.4rem;
  }

  .fc-button {
  font-size: ${theme.font.size.small};
}

.fc-button-primary {
  color: ${theme.font.color.black};
  background-color: white ;
  border-color:  ${theme.color.primaryBorder};
}

.fc-today-button {
  background-color: ${theme.color.slate5};
  color: ${theme.color.slate11};
}
`;